import { IndexedDBCache, AccountConstants } from 'finhelper';
import type { QueryClient } from '@tanstack/react-query';
import type { Dispatch } from 'redux';
import { getRefreshConfig, type RefreshConfigParams } from '#app/constants/invalidationScopes';
import * as interfaceActions from '#app/actions/interface';
import * as accountActions from '#app/actions/account';
import * as authActions from '#app/actions/auth';

/** Action creator — либо без аргументов, либо с params */
type ScopeActionCreator = (params?: RefreshConfigParams) => any;

/**
 * Единый реестр экшенов для всех scope.
 * Имя в dispatchActions конфига → action creator.
 * getUserNotifications требует params.clientId; остальные — без аргументов или только params.
 */
const SCOPE_ACTIONS: Record<string, ScopeActionCreator> = {
  getUserAccounts: () => (accountActions.getUserAccounts as any)(),
  getUserNotifications: (params) =>
    (interfaceActions.getUserNotifications as any)({
      clientId: params?.clientId ?? 0,
    }),
  /** Список загруженных отчётов АТОН/БКС — для scope BROKER_REPORT_FILES. */
  getAtonFiles: () => (interfaceActions.getAtonFiles as any)(),
  getClients: () => (interfaceActions.getClients as any)(),
  /** Сброс фильтра типов счетов (все типы) — для scope ACCOUNTS. */
  resetAvailableTypes: () =>
    (accountActions.setAvailableTypes as any)(
      AccountConstants.types().map((t: { id: number }) => t.id)
    ),
  /** Обновление подкатегорий в Redux — для scope SUBCATEGORY. */
  getActiveSubcategories: () => (interfaceActions.getActiveSubcategories as any)(),
  /** Хеш баланса в интерфейсе + очистка кеша BALANCE в IndexedDB. */
  handleReloadBalance: () => (interfaceActions.handleReloadBalance as any)(),
  /** Обновление пользователя/сессии + каскадный refresh через auth saga. */
  getUser: () => (authActions.getUser as any)({}),
};

export interface InvalidateByScopeParams {
  queryClient: QueryClient;
  dispatch: Dispatch;
  /** Параметры для динамических scope (clientId — BROKER_SYNC/ZENMONEY_SYNC, obligationId — OBLIGATIONS_EDIT). */
  params?: RefreshConfigParams;
}

export interface InvalidateByScopeQueryOnlyParams {
  queryClient: QueryClient;
  /** Параметры для динамических scope (clientId — BROKER_SYNC/ZENMONEY_SYNC, obligationId — OBLIGATIONS_EDIT). */
  params?: RefreshConfigParams;
}

/**
 * Инвалидирует данные по scope.
 * Единственный источник истины: IndexedDB + TanStack Query + Redux dispatch.
 * Вызывается из useDataRefresh (хук) и из webSocketMiddleware (вне React).
 */
export async function invalidateByScope(
  scope: string,
  { queryClient, dispatch, params = {} }: InvalidateByScopeParams
): Promise<void> {
  const config = getRefreshConfig(scope, params);
  const { queryKeys = [], cachePrefixes = [], dispatchActions = [] } = config;

  const hasWork =
    (queryKeys && queryKeys.length > 0) ||
    (cachePrefixes && cachePrefixes.length > 0) ||
    (Array.isArray(dispatchActions) && dispatchActions.length > 0);
  if (!hasWork) {
    return;
  }

  try {
    for (let i = 0; i < (cachePrefixes?.length ?? 0); i++) {
      await IndexedDBCache.clearByPattern(cachePrefixes[i]);
    }

    for (let i = 0; i < (queryKeys?.length ?? 0); i++) {
      queryClient.invalidateQueries({
        queryKey: queryKeys[i] as unknown[],
        exact: false,
      });
    }

    if (Array.isArray(dispatchActions) && dispatchActions.length > 0) {
      dispatchActions.forEach((actionName) => {
        const actionCreator = SCOPE_ACTIONS[actionName];
        if (actionCreator) {
          const action = actionCreator(params);
          if (action != null) {
            dispatch(action);
          }
        } else {
          console.warn('[invalidateByScope] Неизвестный action в dispatchActions:', actionName);
        }
      });
    }
  } catch (err) {
    // При ошибке всё равно вызываем экшены из scope
    if (Array.isArray(dispatchActions) && dispatchActions.length > 0) {
      dispatchActions.forEach((actionName) => {
        const actionCreator = SCOPE_ACTIONS[actionName];
        if (actionCreator) {
          const action = actionCreator(params);
          if (action != null) {
            dispatch(action);
          }
        }
      });
    }
    throw err;
  }
}

/**
 * Query/cache-only вариант для saga flow:
 * очищает IndexedDB и инвалидирует TanStack Query, но НЕ диспатчит Redux actions.
 * Redux-обновления в saga выполняются через yield put(...).
 */
export async function invalidateByScopeQueryOnly(
  scope: string,
  { queryClient, params = {} }: InvalidateByScopeQueryOnlyParams
): Promise<void> {
  const config = getRefreshConfig(scope, params);
  const { queryKeys = [], cachePrefixes = [] } = config;

  const hasWork =
    (queryKeys && queryKeys.length > 0) || (cachePrefixes && cachePrefixes.length > 0);
  if (!hasWork) {
    return;
  }

  for (let i = 0; i < (cachePrefixes?.length ?? 0); i++) {
    await IndexedDBCache.clearByPattern(cachePrefixes[i]);
  }

  for (let i = 0; i < (queryKeys?.length ?? 0); i++) {
    queryClient.invalidateQueries({
      queryKey: queryKeys[i] as unknown[],
      exact: false,
    });
  }
}
